/**
 * Product Hooks - React Query Key Factory
 * 
 * Centralized query key management for all product-related queries.
 * 
 * Benefits:
 * - Prevents cache key collisions
 * - Makes cache invalidation simple and predictable
 * - Single place to manage cache configuration
 * - Type-safe query keys with autocomplete
 * 
 * Usage:
 * ```typescript
 * // In a hook:
 * useQuery({
 *   queryKey: productQueries.lists.byCategory(categoryId),
 *   queryFn: fetchProducts,
 * });
 * 
 * // Invalidate cache:
 * queryClient.invalidateQueries({ queryKey: productQueries.lists._def });
 * ```
 */

import type { ProductSortType, ProductFilters } from './types';

// ============================================================================
// QUERY KEY FACTORY
// ============================================================================

/**
 * Product query keys organized by scope
 * 
 * Structure:
 * - all: Root key for all product queries
 * - lists: Keys for product list queries (with filters, sorting, etc.)
 * - details: Keys for single product detail queries
 * - search: Keys for search queries
 * - filters: Keys for filter/category queries
 */
export const productQueries = {
  // Root key
  all: ['products'] as const,
  
  // Product lists
  lists: {
    // All lists
    all: () => [...productQueries.all, 'list'] as const,
    
    // By category
    byCategory: (categoryId: number, options?: {
      sortType?: ProductSortType;
      page?: number;
      pageSize?: number;
      filters?: ProductFilters;
    }) => [
      ...productQueries.lists.all(),
      'category',
      categoryId,
      options,
    ] as const,
    
    // Featured products
    featured: (limit?: number) => [
      ...productQueries.lists.all(),
      'featured',
      { limit },
    ] as const,
    
    // On sale products
    onSale: (limit?: number) => [
      ...productQueries.lists.all(),
      'onSale',
      { limit },
    ] as const,
    
    // Recent/newest products
    recent: (limit?: number) => [
      ...productQueries.lists.all(),
      'recent',
      { limit },
    ] as const,
    
    // Popular products
    popular: (limit?: number) => [
      ...productQueries.lists.all(),
      'popular',
      { limit },
    ] as const,
    
    // Custom filtered list
    filtered: (options: {
      categoryId?: number;
      sortType?: ProductSortType;
      page?: number;
      pageSize?: number;
      filters?: ProductFilters;
    }) => [
      ...productQueries.lists.all(),
      'filtered',
      options,
    ] as const,
  },
  
  // Product details
  details: {
    // All details
    all: () => [...productQueries.all, 'detail'] as const,
    
    // By ID
    byId: (id: string, options?: {
      includeRecommendations?: boolean;
      includeWishlistStatus?: boolean;
    }) => [
      ...productQueries.details.all(),
      id,
      options,
    ] as const,
  },
  
  // Search
  search: {
    // All searches
    all: () => [...productQueries.all, 'search'] as const,
    
    // By query
    byQuery: (query: string, options?: {
      categoryId?: number;
      priceRange?: { min: number; max: number };
      limit?: number;
    }) => [
      ...productQueries.search.all(),
      query,
      options,
    ] as const,
  },
  
  // Filters and metadata
  filters: {
    // All filter queries
    all: () => [...productQueries.all, 'filters'] as const,
    
    // Categories for filters
    categories: () => [
      ...productQueries.filters.all(),
      'categories',
    ] as const,
    
    // Brands for filters
    brands: (categoryId?: number) => [
      ...productQueries.filters.all(),
      'brands',
      categoryId,
    ] as const,
    
    // Price range
    priceRange: (categoryId?: number) => [
      ...productQueries.filters.all(),
      'priceRange',
      categoryId,
    ] as const,
  },
};

// ============================================================================
// CACHE TIME CONSTANTS
// ============================================================================

/**
 * Cache time configuration for different query types
 * 
 * These values determine how long data stays "fresh" before refetching.
 * Adjust based on how frequently your data changes.
 */
export const PRODUCT_CACHE_TIME = {
  // Product lists (5 minutes)
  // Good balance: fresh enough for most users, reduces server load
  lists: 5 * 60 * 1000,
  
  // Product details (10 minutes)
  // Details change less frequently than lists
  details: 10 * 60 * 1000,
  
  // Search results (3 minutes)
  // Search results should be relatively fresh
  search: 3 * 60 * 1000,
  
  // Filter options (30 minutes)
  // Categories, brands, etc. change infrequently
  filters: 30 * 60 * 1000,
  
  // Featured products (10 minutes)
  // These are curated and don't change often
  featured: 10 * 60 * 1000,
  
  // On sale products (5 minutes)
  // Sales may change frequently
  onSale: 5 * 60 * 1000,
};

// ============================================================================
// CACHE INVALIDATION UTILITIES
// ============================================================================

/**
 * Type-safe cache invalidation helpers
 * 
 * Usage:
 * ```typescript
 * import { invalidateProductQueries } from '@/hooks/products/queryKeys';
 * 
 * // Invalidate all product queries
 * invalidateProductQueries.all(queryClient);
 * 
 * // Invalidate specific category
 * invalidateProductQueries.category(queryClient, 'cat-123');
 * ```
 */
export const invalidateProductQueries = {
  /**
   * Invalidate ALL product queries
   */
  all: (queryClient: any) => {
    return queryClient.invalidateQueries({ queryKey: productQueries.all });
  },
  
  /**
   * Invalidate all product lists
   */
  lists: (queryClient: any) => {
    return queryClient.invalidateQueries({ queryKey: productQueries.lists.all() });
  },
  
  /**
   * Invalidate specific category
   */
  category: (queryClient: any, categoryId: number) => {
    return queryClient.invalidateQueries({
      queryKey: [...productQueries.all, 'list', 'category', categoryId],
    });
  },
  
  /**
   * Invalidate featured products
   */
  featured: (queryClient: any) => {
    return queryClient.invalidateQueries({
      queryKey: [...productQueries.all, 'list', 'featured'],
    });
  },
  
  /**
   * Invalidate product details
   */
  detail: (queryClient: any, productId?: string) => {
    if (productId) {
      return queryClient.invalidateQueries({
        queryKey: [...productQueries.all, 'detail', productId],
      });
    }
    return queryClient.invalidateQueries({
      queryKey: productQueries.details.all(),
    });
  },
  
  /**
   * Invalidate search results
   */
  search: (queryClient: any) => {
    return queryClient.invalidateQueries({
      queryKey: productQueries.search.all(),
    });
  },
};

// ============================================================================
// QUERY OPTIONS PRESETS
// ============================================================================

/**
 * Common React Query options for product queries
 * Use these for consistent behavior across all hooks
 */
export const PRODUCT_QUERY_OPTIONS = {
  /**
   * Default options for product list queries
   */
  lists: {
    staleTime: PRODUCT_CACHE_TIME.lists,
    gcTime: PRODUCT_CACHE_TIME.lists * 2, // Keep in cache 2x longer
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1,
  },
  
  /**
   * Default options for product detail queries
   */
  details: {
    staleTime: PRODUCT_CACHE_TIME.details,
    gcTime: PRODUCT_CACHE_TIME.details * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
  },
  
  /**
   * Default options for search queries
   */
  search: {
    staleTime: PRODUCT_CACHE_TIME.search,
    gcTime: PRODUCT_CACHE_TIME.search * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: true, // Search should refetch on mount
    retry: 1,
  },
  
  /**
   * Default options for filter queries
   */
  filters: {
    staleTime: PRODUCT_CACHE_TIME.filters,
    gcTime: PRODUCT_CACHE_TIME.filters * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
  },
};

// ============================================================================
// TYPES
// ============================================================================

/**
 * Type helper for query keys
 */
export type ProductQueryKey = ReturnType<
  typeof productQueries.lists.byCategory |
  typeof productQueries.lists.featured |
  typeof productQueries.details.byId |
  typeof productQueries.search.byQuery
>;
